/* External */
import {
  memo, useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { useHistory } from 'react-router-dom';

/* Components */
import Header from '../Header';
import WelcomePageContainer from './WelcomePageContainer';
import FocusableButton from '../../../components/FocusableButton';
import Vanity from '../Vanity';
import Confirmation from '../../../components/Confirmation';

/* Utils */
import { useWelcomePage } from './utils/useWelcomePage';
import { getLink, Routes } from '../../../utils/router';
import platform from '../../../utils/platform';
import { useOnBackPress, useOnKeySequence } from '../../../utils/keyDownEvents';
import { BETA_APP_URL } from '../../../utils/env';
import { useCountry } from '../../../providers/ConfigProvider';
import { useGtmLocalContext } from '../../../providers/GtmLocalProvider';
import { GTM_CATEGORIES, GTM_EVENT_NAMES, GTM_SUBCATEGORIES } from '../../../utils/googleTags';
import { STORAGE_COUNTRY } from '../../../utils/constants';

/* Styles */
import './WelcomePage.scss';

const { up, down } = platform.keyCodes.channel;
const BETA_KEY_SEQUENCE = [up, up, down, up, down, down, up, up, down, down];

function WelcomePage({ realFocusKey, setFocus }) {
  const loginButtonFocusKey = `${realFocusKey}-login-button`;
  const [showBetaConfirmation, setShowBetaConfirmation] = useState(false);
  const history = useHistory();
  const { loginButton } = useWelcomePage();
  const country = useCountry();

  const { commonTaggingEvent } = useGtmLocalContext();
  const tagsMountRef = useRef(true);

  const handleLoginTagEvents = useCallback(({ eventName }) => {
    commonTaggingEvent({
      category: GTM_CATEGORIES.LOGIN_DATA,
      subCategory: GTM_SUBCATEGORIES.LOGIN.HOME,
      eventName,
    });
  }, [commonTaggingEvent]);

  useEffect(() => {
    if (tagsMountRef.current && country) {
      handleLoginTagEvents({ eventName: GTM_EVENT_NAMES.PAGE_VIEW });
      tagsMountRef.current = false;
    }
  }, [handleLoginTagEvents, country]);

  useEffect(() => {
    if (country) {
      localStorage.setItem(STORAGE_COUNTRY, country);
    }
  }, [country]);

  const handleLogin = useCallback(() => {
    handleLoginTagEvents({ eventName: GTM_EVENT_NAMES.LOGIN.ENTER_LOGIN });
    history.push(getLink(Routes.Public.PinLogin));
  }, [handleLoginTagEvents, history]);

  useOnBackPress({ listener: platform.closeApp, when: true });

  useEffect(() => {
    setFocus(loginButtonFocusKey);
  }, [loginButtonFocusKey, setFocus]);

  const displayBetaConfirmation = useCallback(() => {
    setShowBetaConfirmation(true);
  }, []);

  const hideBetaConfirmation = useCallback(() => {
    setShowBetaConfirmation(false);
    setFocus(loginButtonFocusKey);
  }, [setFocus, loginButtonFocusKey]);

  const loadBetaApp = useCallback(() => {
    // eslint-disable-next-line no-restricted-globals -- We need to do a redirection to the beta app
    location.href = BETA_APP_URL;
  }, []);

  useOnKeySequence({
    listener: displayBetaConfirmation,
    sequence: BETA_KEY_SEQUENCE,
    when: platform.isWebOS || false,
  });

  return (
    <WelcomePageContainer>
      <section className="dtv-html5-c-welcome-page">
        <Header />
        <FocusableButton
          onEnterPress={handleLogin}
          focusKey={loginButtonFocusKey}
          className="dtv-html5-c-welcome-page__login-button"
        >
          { loginButton }
        </FocusableButton>
        <Vanity className="dtv-html5-c-welcome-page__vanity" />
      </section>
      {showBetaConfirmation && (
        <Confirmation
          acceptTranslationId="common.accept"
          cancelTranslationId="common.cancel"
          messageTranslationId="welcomePage.loadBeta"
          onAcceptPress={loadBetaApp}
          onCancelPress={hideBetaConfirmation}
        />
      )}
    </WelcomePageContainer>
  );
}

WelcomePage.propTypes = {
  realFocusKey: PropTypes.string.isRequired,
  setFocus: PropTypes.func.isRequired,
};

export default memo(withFocusable()(memo(WelcomePage)));
